import { cn } from '@/exxonim/utils/cn'
import { forwardRef, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { preloadRoute } from '@/exxonim/preloadRegistry'
import { normalizePathname } from '@/exxonim/routes'

/* ═══════════════════════════════════════════════════════════════
 * Button - Exxonim Design System Primitive
 *
 * 4-tier sizing with 4 variant styles + disabled / loading states.
 * Replaces all hand-coded button classes across the site.
 *
 * TIERS (static heights, responsive padding):
 *   hero     - h-12 (48px) - Reserved for top-level hero banners only
 *   standard - h-10 (40px) - Default for all page buttons: CTAs, form submits,
 *                            cookie consent, error recovery, inline actions
 *   compact  - h-8  (32px) - Navigation components only (sidebar, mega menu,
 *                            bottom bars; invisible touch-target expansion on mobile)
 *   icon     - w-9 h-9 (36px) - Close, share, arrows, toggles
 *
 * VARIANTS:
 *   primary   - bg-accent text-accent-contrast (the main action)
 *   secondary - border border-border-soft bg-surface/80 text-text (alternative)
 *   outline   - border border-accent text-accent (less prominent)
 *   ghost     - bg-transparent text-text (subtle / contextual)
 *
 * USAGE:
 *   <Button size="standard" variant="primary" href="/contact">Contact Us</Button>
 *   <Button size="standard" variant="secondary" onClick={handleClick}>Cancel</Button>
 *   <Button size="icon" variant="ghost" aria-label="Close"><XIcon /></Button>
 * ═══════════════════════════════════════════════════════════════ */

type ButtonSize = 'hero' | 'standard' | 'compact' | 'icon'
type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'inverse' | 'inverseOutline'

/** Props that Button consumes internally. */
interface ButtonOwnProps {
  /** Visual tier - controls height, padding, and typography. */
  size?: ButtonSize
  /** Color / style variant. */
  variant?: ButtonVariant
  /** If provided, renders as <a> instead of <button>. */
  href?: string
  /** Button content. */
  children: ReactNode
  /** Click handler - works on both <button> and <a> elements. */
  onClick?: React.MouseEventHandler<HTMLElement>
  /** Additional classes - merged last so they can override. */
  className?: string
  /** Whether the button is disabled. */
  disabled?: boolean
  /** Whether the button is in a loading state (shows spinner, disables interaction). */
  isLoading?: boolean
  /** HTML type attribute - defaults to "button" for <button>, ignored for <a>. */
  type?: 'button' | 'submit' | 'reset'
  /** HTML target attribute for <a> links. */
  target?: string
  /** HTML rel attribute for <a> links. */
  rel?: string
  /** ARIA label - required for icon-only buttons. */
  'aria-label'?: string
}

/** Extra HTML attributes (ARIA, data-*, etc.) passed through to the underlying element. */
type ButtonRestProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement> &
    React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof ButtonOwnProps | 'ref'
>

export type ButtonProps = ButtonOwnProps & ButtonRestProps

/* ── Size styles ─────────────────────────────────────────────── */
/* Minimal button geometry: 8px radius (not pills), weight 700. Icon buttons
 * stay circular - that matches the reference system too. */
const sizeStyles: Record<ButtonSize, string> = {
  hero: cn(
    'h-12 px-5 sm:px-6 rounded-lg',
    'text-base font-bold',
  ),
  standard: cn(
    'h-11 px-4 sm:px-5 rounded-lg',
    'text-sm font-bold',
  ),
  compact: cn(
    'h-8 px-3 sm:px-4 rounded-lg',
    'text-xs font-bold',
    /* Touch-target expansion: visually 32px but tappable ~48px on mobile.
     * The ::before pseudo-element extends the hit area by 8px above/below
     * without affecting layout or visual appearance. */
    'relative before:absolute before:-top-2 before:-bottom-2 before:left-0 before:right-0',
  ),
  icon: cn(
    'w-9 h-9 rounded-full',
    /* No text sizing - icon only */
  ),
}

/* ── Variant styles ──────────────────────────────────────────── */
const variantStyles: Record<ButtonVariant, string> = {
  primary: cn(
    'bg-accent text-accent-contrast',
    'hover:bg-accent-hover hover:text-accent-contrast hover:shadow-accent-glow',
  ),
  secondary: cn(
    'border border-border-strong bg-surface text-text',
    'hover:bg-surface-soft hover:text-text hover:border-text/40',
  ),
  outline: cn(
    'border border-accent text-accent',
    'hover:bg-accent-soft hover:text-accent',
  ),
  ghost: cn(
    'bg-transparent text-text',
    'hover:bg-accent-soft hover:text-text',
  ),
  /* For use on the teal CTA banner (footer-bg). A teal `primary` would vanish
   * on teal - these read as a solid white pill / a white outline instead. */
  inverse: cn(
    // Fixed brand teal (NOT text-accent - that flips to light teal in dark mode
    // and would be unreadable on the white pill). Always high-contrast on white.
    'bg-white text-[#0f5c63] shadow-sm',
    'hover:bg-white/90 hover:text-[#0b4b51]',
  ),
  inverseOutline: cn(
    'border border-white/50 text-white',
    'hover:bg-white/10 hover:text-white',
  ),
}

/* ── Shared base styles ─────────────────────────────────────── */
const baseStyles = cn(
  'inline-flex items-center justify-center',
  'no-underline',
  'transition-transform duration-200',
  'hover:-translate-y-0.5 active:translate-y-0',
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
  'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0',
)

/* ── Loading spinner ─────────────────────────────────────────── */
function Spinner() {
  return (
    <svg
      className="animate-spin h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle
        className="opacity-25"
        cx="12" cy="12" r="10"
        stroke="currentColor" strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
  )
}

/* ── Component ───────────────────────────────────────────────── */
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(
    {
      size = 'standard',
      variant = 'primary',
      href,
      children,
      onClick,
      className,
      disabled = false,
      isLoading = false,
      type = 'button',
      target,
      rel,
      'aria-label': ariaLabel,
      ...rest
    },
    ref,
  ) {
    const isDisabled = disabled || isLoading
    const classes = cn(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant],
      className,
    )

    const content = isLoading ? (
      <span className="inline-flex items-center gap-2">
        <Spinner />
        {children}
      </span>
    ) : (
      children
    )

    if (href && !isDisabled) {
      // External / non-routable targets (tel:, mailto:, http(s), #, new tab)
      // keep a plain <a> - a full navigation is correct there.
      const isExternal =
        /^(tel:|mailto:|https?:\/\/|#|javascript:)/.test(href) || target === "_blank"
      if (isExternal) {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={href}
            className={classes}
            target={target}
            rel={rel}
            aria-label={ariaLabel}
            onClick={onClick}
            {...rest}
          >
            {content}
          </a>
        )
      }
      // Internal routes: navigate CLIENT-SIDE via the router (no full page
      // reload / boot loader) and preload the target chunk on hover, exactly
      // like SmartLink. Fixes CTAs such as the "Track Consultation" button that
      // used to hard-reload the whole app.
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={href}
          className={classes}
          aria-label={ariaLabel}
          onClick={onClick}
          {...rest}
          onMouseEnter={() => preloadRoute(normalizePathname(href))}
        >
          {content}
        </Link>
      )
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={type}
        onClick={onClick}
        className={classes}
        disabled={isDisabled}
        aria-label={ariaLabel}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {content}
      </button>
    )
  },
)
